import { BoatsetterPromise } from 'components/homepage/boatsetterPromise';
import Experiences from 'components/homepage/experiences';
import { Explore } from 'components/homepage/Explore';
import HomepageHead from 'components/homepage/Head';
import Hero from 'components/homepage/hero';
import ParagraphPicPair from 'components/homepage/ParagraphPicPair';
import SearchBar from 'components/homepage/searchBar';
import SEOLinks from 'components/homepage/seoLinks';
import { getOrganizationSchema, getWebsiteSchema, howWorksData } from 'components/homepage/utils';
import DefaultLayout from 'components/layouts/DefaultLayout';
import { mediaLgMax, mediaSmMax } from 'helpers/breakpoints';
import styled from 'styled-components';
import { nextOptimizedImageUrl } from 'utils/images';

/**
 * These next 4 lines centralize the hero image, and it's uses in the following ways:
 * - The hero image that appears on the page (obviously)
 * - The image that the SEO product schema uses (Google results)
 * - The image that Facebook's Open Graph uses (Facebook shares)
 * - The image that Twitter's own schema uses (Twitter shares)
 *
 * We previously went years without updating these images, so keeping it at the top of this file and passing these
 * values to the various components that use them seems like a good practice.
 */
const heroImageDesktopFile = 'hero-april-2026.jpg';
const heroImageMobileFile = 'hero-april-2026.jpg';
const metaDataImagePath = `/images/homepage/hero/${heroImageDesktopFile}`;
const metaDataImageUrl = nextOptimizedImageUrl({ src: metaDataImagePath, quality: 100, width: 1200 });

/**
 * There are seasonal changes to this file. This includes re-using past components in new ways. If you are adding a new
 * component, check out this commit: https://github.com/boatsetter/marketplace-spa/blob/d323ec3728c4cdc97d1aba32019d51f31f5ca0a5/pages/index.tsx
 * These previous components have been deleted due to disuse but may be useful for reference.
 */

const HomePage = () => {
  const metaTitle = 'Boat Rentals, Charter Boats Near Me, Yacht Rentals & More | Boatsetter';
  const metaDescription =
    'Rent a boat today, with or without a boat captain, and get out on the water! Browse our huge selection of boat rentals near you including yachts, pontoons, sailboats, fishing boats, wakeboarding boats, Catamarans & more!';

  return (
    <DefaultLayout isHome isHomePage noFollow={false} white ownerNav={false}>
      <HomepageWrapper id="homePage">
        <HomepageHead metaTitle={metaTitle} metaDescription={metaDescription} previewImage={metaDataImageUrl} />

        {/* The Hero and SearchBar are exactly what they sound like. The SearchBar displays over top of the Hero. */}
        <Hero images={{ desktop: heroImageDesktopFile, mobile: heroImageMobileFile }} />
        <SearchBar />

        {/*  Explore is the carousel of location cards which link to location-specific search pages. */}
        <Explore />
        {/* BoatsetterPromise is an informational section. There is no interactivity. */}
        <BoatsetterPromise data={howWorksData} />
        {/* Experiences is the carousel of two pages, each containing experience-specific cards with search links. */}
        <Experiences />

        <ParagraphPicPair
          imageSrc="spa/homepage/adventure-is-calling.jpg"
          altText="a catamaran in the water in Puerto Rico"
          title="Adventure is calling"
          content="Ready to reel one in? From coastal hotspots to hidden gems, check out top-rated fishing charters in our most loved destinations."
          linkText="Browse boats"
          linkUrl="/fishing"
        />

        {/* SEOLinks is a tabbed section of links to SEO pages. */}
        <SEOLinks />

        {/* SEO structured data */}
        <script dangerouslySetInnerHTML={{ __html: getWebsiteSchema() }} type="application/ld+json" />
        <script dangerouslySetInnerHTML={{ __html: getOrganizationSchema() }} type="application/ld+json" />
      </HomepageWrapper>
    </DefaultLayout>
  );
};

export default HomePage;

const HomepageWrapper = styled.div`
  * {
    font-family: 'AvenirNext', 'Avenir', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }

  .info > div {
    @media ${mediaSmMax} {
      height: 160px;
    }
  }

  .cap {
    max-width: 1076px;
    width: 100%;
    margin: auto;
    position: relative;
    //overflow-y: hidden;

    @media ${mediaLgMax} {
      margin: 0 40px;
      width: unset;
    }

    @media ${mediaSmMax} {
      margin: 0 20px;
      width: unset;
    }
  }

  h3 {
    font-size: 24px;
    line-height: 28px;

    @media ${mediaSmMax} {
      font-size: 20px;
      line-height: 24px;
    }
  }

  h4 {
    font-size: 18px;
    font-weight: bold;

    @media ${mediaSmMax} {
      font-size: 16px;
      line-height: 28px;
    }
  }
`;
